import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createTextVNode as _createTextVNode, toDisplayString as _toDisplayString, unref as _unref, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, createCommentVNode as _createCommentVNode, createVNode as _createVNode, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "flex flex-col h-full justify-between" }
const _hoisted_2 = { class: "space-y-6" }
const _hoisted_3 = { class: "grid grid-cols-3 gap-4" }
const _hoisted_4 = { class: "bg-white p-4 rounded-lg shadow-sm border border-gray-200" }
const _hoisted_5 = { class: "text-2xl font-bold text-gray-800" }
const _hoisted_6 = { class: "bg-white p-4 rounded-lg shadow-sm border border-gray-200" }
const _hoisted_7 = { class: "text-2xl font-bold text-gray-800" }
const _hoisted_8 = { class: "bg-white p-4 rounded-lg shadow-sm border border-gray-200" }
const _hoisted_9 = { class: "text-2xl font-bold text-gray-800" }
const _hoisted_10 = { class: "bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden" }
const _hoisted_11 = { class: "flex overflow-x-auto border-b border-gray-200" }
const _hoisted_12 = ["onClick"]
const _hoisted_13 = { class: "flex flex-col items-start gap-1" }
const _hoisted_14 = { class: "text-xs font-semibold" }
const _hoisted_15 = { class: "text-xs" }
const _hoisted_16 = {
  key: 0,
  class: "h-6 w-px bg-gray-300 self-center"
}
const _hoisted_17 = { class: "overflow-x-auto" }
const _hoisted_18 = {
  key: 0,
  class: "text-center py-8 text-gray-500"
}
const _hoisted_19 = {
  key: 1,
  class: "min-w-full"
}
const _hoisted_20 = { class: "divide-y divide-gray-200" }
const _hoisted_21 = { class: "px-6 py-4 whitespace-nowrap text-sm text-gray-900" }
const _hoisted_22 = { class: "px-6 py-4 text-sm text-gray-500 max-w-xs" }
const _hoisted_23 = { class: "px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" }
const _hoisted_24 = { class: "px-6 py-4 whitespace-nowrap text-sm text-gray-900" }
const _hoisted_25 = { class: "px-6 py-4 whitespace-nowrap text-sm text-gray-900" }
const _hoisted_26 = { class: "px-6 py-4 whitespace-nowrap text-sm text-gray-900" }
const _hoisted_27 = ["onClick"]
const _hoisted_28 = { class: "w-full text-center" }
const _hoisted_29 = ["disabled"]
const _hoisted_30 = {
  key: 0,
  class: "flex items-center justify-center"
}
const _hoisted_31 = { key: 1 }

import { computed, ref, watch } from 'vue';
import { ChallengeConfiguration, ChallengeProgressEntry } from '@/types/v2/task/challenge/configuration.types';
import { useRouter } from 'vue-router';
import { useChallenge } from "@/composables/tasks/challenge/useChallenge";
import { useChallengeConfigurations } from "@/composables/tasks/challenge/useChallengeConfigurations";
import { useRecord } from "@/composables/tasks/challenge/useChallengeRecords";
import { useNotifications } from "@/composables/useNotifications";

import FooterActions from '@/components/v2/Tasks/Challenge/Buttons/ActionButtons/FooterActions.vue';
import ChallengeRevisionModal from "@/components/v2/Tasks/Challenge/Modals/ChallengeRevisionModal.vue";
import { useWeight } from "@/composables/tasks/challenge/useChallengeWeight";
import { useTask } from "@/composables/tasks/useTask";
import { TasksService } from "@/services/v2/tasks";
import { useEquipment } from "@/composables/tasks/useEquipment";
import TaskCompletionBlockedModal from "@/components/v2/Tasks/Task/TaskCompletionBlockedModal.vue";

// ===================================================================
// Composables
// ===================================================================

export default /*@__PURE__*/_defineComponent({
  __name: 'OverviewView',
  setup(__props) {

const router = useRouter();
const { activeTask } = useTask();
const { configurations } = useChallenge();
const { getConfigurationTags } = useChallengeConfigurations();
const { getChallengeRecordsByConfigKey } = useRecord();
const { getAverageWeightByConfigKeyChallengeRecords } = useWeight();
const { showTaskCompletionSuccess, showError } = useNotifications();
const { getWeightIdCompletionValidation } = useEquipment();

// ===================================================================
// State
// ===================================================================
const activeConfigKey = ref<string>('');
const challengeRevisionModal = ref<boolean>(false);
const selectedEntry = ref<ChallengeProgressEntry | null>(null);
const selectedEntryIndex = ref<number>(-1);
const isFinishing = ref<boolean>(false);
const completionBlockModal = ref<boolean>(false);

// ===================================================================
// Actions
// ===================================================================
// Go back
const goBack = () => router.go(-1);

// Open revisions modal
const openRevisionsModal = (entry: ChallengeProgressEntry, entryIndex: number) => {
    challengeRevisionModal.value = true;
    selectedEntry.value = entry;
    selectedEntryIndex.value = entryIndex;
}

// Finish task handler
const handleFinishTask = async () => {
    if (!activeTask.value) return;

    const validation = getWeightIdCompletionValidation();
    if (!validation.isValid) {
        completionBlockModal.value = true;
        return;
    }
    
    isFinishing.value = true;
    
    try {
        await TasksService.finishTask(activeTask.value);
        showTaskCompletionSuccess(activeTask.value.title);
    } catch (error) {
        console.error('Error completing challenge task:', error);
        showError("Task Completion Failed", "Failed to complete the challenge task. Please try again.");
    } finally {
        isFinishing.value = false;
    }
}

// ===================================================================
// Computed
// ===================================================================
const challengeRecords = computed<ChallengeProgressEntry[] | []>(() => getChallengeRecordsByConfigKey(activeConfigKey.value));
const averageWeight = computed<number | string>(() => getAverageWeightByConfigKeyChallengeRecords(activeConfigKey.value));
const completionValidation = computed(() => getWeightIdCompletionValidation());
const getDestinationTankName = (config: ChallengeConfiguration): string => {
    const destinationTankName = (config as any)?.destinationTank?.name || (config as any)?.destinationTank?.label;
    if (destinationTankName) return destinationTankName;
    return (config as any)?.tank?.name || (config as any)?.tank?.label || 'No destination tank';
};
const getTagCombinationLabel = (config: ChallengeConfiguration): string => {
    const tagTitles = getConfigurationTags(config).map(tag => tag.title);
    return tagTitles.length ? tagTitles.join(' + ') : 'Unmarked';
};

// ===================================================================
// Watchers
// ===================================================================
// Watch for configurations composable state change
watch(
    () => configurations.value,
    (newConfigurations) => {
        if (!activeConfigKey.value && newConfigurations.length > 0) {
            activeConfigKey.value = newConfigurations[0].key;
        }
    },
    { immediate: true }
);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", { class: "flex justify-between items-center" }, [
        _cache[3] || (_cache[3] = _createElementVNode("h1", { class: "text-2xl font-bold text-gray-800" }, "Challenge Administration Records", -1)),
        _createElementVNode("button", {
          onClick: goBack,
          class: "text-gray-600 hover:text-gray-800 flex items-center gap-1 text-sm"
        }, _cache[2] || (_cache[2] = [
          _createElementVNode("svg", {
            xmlns: "http://www.w3.org/2000/svg",
            class: "h-4 w-4",
            viewBox: "0 0 24 24",
            fill: "none",
            stroke: "currentColor",
            "stroke-width": "1.2",
            "stroke-linecap": "round",
            "stroke-linejoin": "round"
          }, [
            _createElementVNode("path", { d: "M19 12H5M12 19l-7-7 7-7" })
          ], -1),
          _createTextVNode(" Back ")
        ]))
      ]),
      _cache[8] || (_cache[8] = _createElementVNode("span", { class: "w-full bg-secondary h-[1px] block" }, null, -1)),
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("div", _hoisted_4, [
          _cache[4] || (_cache[4] = _createElementVNode("h3", { class: "text-sm font-medium text-gray-500 mb-1" }, "Total Challenged Fish", -1)),
          _createElementVNode("p", _hoisted_5, _toDisplayString(challengeRecords.value.reduce((sum, record) => sum + (record.challenged || 0), 0)), 1)
        ]),
        _createElementVNode("div", _hoisted_6, [
          _cache[5] || (_cache[5] = _createElementVNode("h3", { class: "text-sm font-medium text-gray-500 mb-1" }, [
            _createTextVNode("Average Weight "),
            _createElementVNode("span", { class: "text-xs" }, "(grams)")
          ], -1)),
          _createElementVNode("p", _hoisted_7, _toDisplayString(averageWeight.value), 1)
        ]),
        _createElementVNode("div", _hoisted_8, [
          _cache[6] || (_cache[6] = _createElementVNode("h3", { class: "text-sm font-medium text-gray-500 mb-1" }, "Total Records", -1)),
          _createElementVNode("p", _hoisted_9, _toDisplayString(challengeRecords.value.length), 1)
        ])
      ]),
      _createElementVNode("div", _hoisted_10, [
        _createElementVNode("div", _hoisted_11, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(configurations), (config, index) => {
            return (_openBlock(), _createElementBlock(_Fragment, {
              key: config.key
            }, [
              _createElementVNode("button", {
                onClick: ($event: any) => (activeConfigKey.value = config.key),
                class: _normalizeClass(["px-4 py-2 text-sm font-medium whitespace-nowrap flex items-center gap-1", activeConfigKey.value === config.key
                              ? 'text-primary border-b-2 border-primary'
                              : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'])
              }, [
                _createElementVNode("div", _hoisted_13, [
                  _createElementVNode("span", _hoisted_14, _toDisplayString(getDestinationTankName(config)), 1),
                  _createElementVNode("span", _hoisted_15, _toDisplayString(getTagCombinationLabel(config)), 1)
                ])
              ], 10, _hoisted_12),
              (index < _unref(configurations).length - 1)
                ? (_openBlock(), _createElementBlock("div", _hoisted_16))
                : _createCommentVNode("", true)
            ], 64))
          }), 128))
        ]),
        _createElementVNode("div", _hoisted_17, [
          (!challengeRecords.value.length)
            ? (_openBlock(), _createElementBlock("div", _hoisted_18, " No challenge records for this configuration "))
            : (_openBlock(), _createElementBlock("table", _hoisted_19, [
                _cache[7] || (_cache[7] = _createElementVNode("thead", { class: "bg-gray-50" }, [
                  _createElementVNode("tr", null, [
                    _createElementVNode("th", { class: "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" }, " # "),
                    _createElementVNode("th", { class: "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" }, " Comment "),
                    _createElementVNode("th", { class: "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" }, " Challenged Fish "),
                    _createElementVNode("th", { class: "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" }, " Weight "),
                    _createElementVNode("th", { class: "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" }, " Challenge Duration "),
                    _createElementVNode("th", { class: "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" }, " No. Revisions "),
                    _createElementVNode("th", { class: "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" }, " Actions ")
                  ])
                ], -1)),
                _createElementVNode("tbody", _hoisted_20, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(challengeRecords.value, (record, index) => {
                    return (_openBlock(), _createElementBlock("tr", {
                      key: index,
                      class: "hover:bg-gray-50"
                    }, [
                      _createElementVNode("td", _hoisted_21, _toDisplayString(index + 1), 1),
                      _createElementVNode("td", _hoisted_22, _toDisplayString(record.comment || 'No comment'), 1),
                      _createElementVNode("td", _hoisted_23, _toDisplayString(record.challenged || 0), 1),
                      _createElementVNode("td", _hoisted_24, _toDisplayString(record.weight || 'N/A'), 1),
                      _createElementVNode("td", _hoisted_25, _toDisplayString(record.challengeDuration || 'N/A') + " " + _toDisplayString(record.challengeDuration ? 'minutes' : ''), 1),
                      _createElementVNode("td", _hoisted_26, _toDisplayString(record.revisions?.length || 0), 1),
                      _createElementVNode("td", null, [
                        _createElementVNode("button", {
                          onClick: ($event: any) => (openRevisionsModal(record, index)),
                          class: "px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-100 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                        }, " Add Revision ", 8, _hoisted_27)
                      ])
                    ]))
                  }), 128))
                ])
              ]))
        ])
      ])
    ]),
    _createVNode(ChallengeRevisionModal, {
      entry: selectedEntry.value,
      configKey: activeConfigKey.value,
      entryIndex: selectedEntryIndex.value,
      isOpen: challengeRevisionModal.value,
      onClose: _cache[0] || (_cache[0] = () => challengeRevisionModal.value = false)
    }, null, 8, ["entry", "configKey", "entryIndex", "isOpen"]),
    _createVNode(TaskCompletionBlockedModal, {
      show: completionBlockModal.value,
      reason: completionValidation.value.reason,
      onClose: _cache[1] || (_cache[1] = ($event: any) => (completionBlockModal.value = false))
    }, null, 8, ["show", "reason"]),
    _createVNode(FooterActions, null, {
      middle: _withCtx(() => [
        _createElementVNode("button", {
          onClick: goBack,
          class: "w-full"
        }, " Back ")
      ]),
      end: _withCtx(() => [
        _createElementVNode("div", _hoisted_28, [
          _createElementVNode("button", {
            onClick: handleFinishTask,
            disabled: isFinishing.value,
            class: _normalizeClass(["w-full relative", { 'opacity-50 cursor-not-allowed': isFinishing.value }])
          }, [
            (isFinishing.value)
              ? (_openBlock(), _createElementBlock("span", _hoisted_30, _cache[9] || (_cache[9] = [
                  _createElementVNode("svg", {
                    class: "animate-spin -ml-1 mr-2 h-4 w-4 text-white",
                    xmlns: "http://www.w3.org/2000/svg",
                    fill: "none",
                    viewBox: "0 0 24 24"
                  }, [
                    _createElementVNode("circle", {
                      class: "opacity-25",
                      cx: "12",
                      cy: "12",
                      r: "10",
                      stroke: "currentColor",
                      "stroke-width": "4"
                    }),
                    _createElementVNode("path", {
                      class: "opacity-75",
                      fill: "currentColor",
                      d: "M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    })
                  ], -1),
                  _createTextVNode(" Finishing... ")
                ])))
              : (_openBlock(), _createElementBlock("span", _hoisted_31, " Finish Task "))
          ], 10, _hoisted_29)
        ])
      ]),
      _: 1
    })
  ]))
}
}

})